export const AlertStatus = {
  LIVE: "Live",
  SCHEDULED: "Scheduled",
  PAST: "Past",
  ENDED: "Ended",
  FAILED: "Failed",
  PENDING: "Pending",
  SUCCESS: "Success",
  PARTIALLY_LIVE: "Partially Live",
};

export const formatTimeLeft = (difference: number): string => {
  const days: number = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours: number = Math.floor(
    (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutes: number = Math.floor(
    (difference % (1000 * 60 * 60)) / (1000 * 60),
  );

  let result: string = "";

  if (days > 0) {
    result += `${days}d `;
  }

  if (hours > 0) {
    result += `${hours}h `;
  }

  result += `${minutes}m`;

  return result.trim();
};

export const calculateTimeLeft = (
  startDateTime?: string | Date,
  endDateTime?: string | Date,
  alertStatus?: string,
): string => {
  if (!startDateTime || !endDateTime) {
    return "";
  }

  const currentTime: number = new Date().getTime();
  const startTime: number = new Date(startDateTime).getTime();

  if (
    startTime > currentTime ||
    alertStatus !== AlertStatus.SUCCESS.toLowerCase()
  ) {
    return "";
  }

  const endTime: number = new Date(endDateTime).getTime();
  const timeLeft: number = endTime - currentTime;

  if (timeLeft <= 0) {
    return "";
  }

  return formatTimeLeft(timeLeft);
};

export const calculateStatus = (
  startDateTime?: string | Date,
  endDateTime?: string | Date,
  alertStatus?: string,
  failedDevices: string[] = [],
  successDevices: string[] = [],
  groupAlertEndTime?: string | Date,
): string => {
  if (groupAlertEndTime) {
    return AlertStatus.ENDED;
  }

  if (!startDateTime) {
    return "";
  }

  const currentTime: Date = new Date();
  const startDate: Date = new Date(startDateTime);
  const endDate: Date | null = endDateTime ? new Date(endDateTime) : null;
  const normalizedAlertStatus = alertStatus?.toLowerCase();

  if (startDate > currentTime) {
    return AlertStatus.SCHEDULED;
  }

  if (startDate <= currentTime && (!endDate || endDate > currentTime)) {
    if (!alertStatus) {
      const oneMinuteAfterAlertStart: Date = new Date(startDate);
      oneMinuteAfterAlertStart.setMinutes(startDate.getMinutes() + 1);

      if (oneMinuteAfterAlertStart <= currentTime) {
        return AlertStatus.FAILED;
      }

      return AlertStatus.PENDING;
    }

    const hasSuccessDevices = successDevices.length > 0;
    const hasFailedDevices = failedDevices.length > 0;

    if (hasSuccessDevices && hasFailedDevices) {
      return AlertStatus.PARTIALLY_LIVE;
    }

    if (hasSuccessDevices) {
      return AlertStatus.LIVE;
    }

    if (hasFailedDevices) {
      return AlertStatus.FAILED;
    }

    if (normalizedAlertStatus === AlertStatus.SUCCESS.toLowerCase()) {
      return AlertStatus.LIVE;
    }

    return AlertStatus.PENDING;
  }

  if (endDate && endDate <= currentTime) {
    if (failedDevices?.length > 0 && successDevices?.length === 0) {
      return AlertStatus.FAILED;
    }

    return AlertStatus.PAST;
  }

  return "";
};

export interface IAlertDetail {
  _id: string;
  startDateTime?: string | Date;
  endDateTime?: string | Date;
  timeZone?: string;
  source?: string;
  alertMessage?: string;
  alertHeading?: string;
  severity?: string;
  alertType?: string;
  fipsCode?: string[];
  __v?: number;
}

export interface IListGroupItem {
  _id?: string;
  orgId: string;
  displayName: string;
  parent: string;
  hierarchy?: string;
  __v?: number;
}

export interface IAlertGroup {
  _id: string;
  alertHistoryId?: string;
  alertId?: string;
  templateId?: string | number;
  groupId: string;
  groupName: string;
  jobId?: string;
  groupAlertId?: string;
  groupAlertEndTime?: string | Date;
  alertStatus?: string;
  successDevices?: string[];
  failedDevices?: string[];
  failedDeviceDetails?: string[];
  creationTime?: string | Date;
  isChildRow?: boolean;
  groupStatus?: string;
  parentAlertDetails?: IAlertDetail;
  parentGroupId?: string;
  groupHierarchy?: string;
  groupDisplayName?: string;
  hierarchyLevel?: number;
  subRows?: IAlertGroup[];
  isSearchPlaceholderRow?: boolean;
}

export interface IAlertsApi {
  _id?: string;
  alertId: string;
  alertDetails: IAlertDetail | IAlertDetail[];
  groups?: IAlertGroup[];
  creationTime?: string | Date;
  templateId?: string | number;
  groupId?: string;
  groupName?: string;
  jobId?: string;
  __v?: number;
  alertStatus?: string;
  successDevices?: string[];
  failedDevices?: string[];
  failedDeviceDetails?: string[];
  aggregateStatus?: string;
  groupCount?: number;
  activeGroupCount?: number;
  groupCountLabel?: string;
  createdLabel?: string;
  subRows?: IAlertGroup[];
  isParentRow?: boolean;
  isSearchResultRow?: boolean;
  isSearchDetailsLoaded?: boolean;
}

export interface IAlerts {
  alerts: IAlertsApi[] | null;
  totalCount?: number;
  pageIndex?: number;
  pageSize?: number;
  alertGroupOptions?: string[];
  status: string;
  error: string | null;
  newlyCreatedAlertId?: string;
}

export const getEmptyAlertDetails = (): IAlertDetail => ({
  _id: "",
});

export const getAlertDetails = (
  alertDetails?: IAlertDetail | IAlertDetail[],
): IAlertDetail => {
  if (Array.isArray(alertDetails)) {
    return alertDetails[0] || getEmptyAlertDetails();
  }

  return alertDetails || getEmptyAlertDetails();
};

export const normalizeSeverity = (severity?: string): string => {
  if (!severity) {
    return "";
  }

  if (severity.toLowerCase() === "emergency") {
    return "High";
  }

  return severity.charAt(0).toUpperCase() + severity.slice(1);
};

export const getStatusClassName = (status?: string): string => {
  return (status || "").toLowerCase().replace(/\s+/g, "-");
};

export const formatGroupHierarchyLabel = (
  hierarchy?: string,
  fallbackName: string = "",
): string => {
  const hierarchyParts = (hierarchy || "")
    .split("/")
    .map((hierarchyPart) => hierarchyPart.trim())
    .filter(Boolean);

  if (!hierarchyParts.length) {
    return fallbackName;
  }

  return hierarchyParts.join(" / ");
};

export const calculateGroupStatus = (
  alertDetails: IAlertDetail,
  group: IAlertGroup,
): string => {
  const failedDevices = group.failedDevices?.length
    ? group.failedDevices
    : group.failedDeviceDetails || [];

  const fallbackEndDateTime =
    group.groupAlertEndTime || alertDetails.endDateTime;

  const calculatedStatus = calculateStatus(
    alertDetails.startDateTime,
    fallbackEndDateTime,
    group.alertStatus,
    failedDevices,
    group.successDevices || [],
    group.groupAlertEndTime,
  );

  if (
    calculatedStatus === AlertStatus.PAST &&
    alertDetails.endDateTime &&
    new Date(alertDetails.endDateTime).getTime() <= new Date().getTime()
  ) {
    return AlertStatus.ENDED;
  }

  return calculatedStatus;
};

export const calculateAggregateStatus = (groupStatuses: string[]): string => {
  if (!groupStatuses.length) {
    return "";
  }

  const liveStatuses = [AlertStatus.LIVE];
  const partialStatuses = [AlertStatus.PARTIALLY_LIVE];
  const terminalStatuses = [
    AlertStatus.ENDED,
    AlertStatus.PAST,
    AlertStatus.FAILED,
  ];

  const areAllScheduled = groupStatuses.every(
    (status) => status === AlertStatus.SCHEDULED,
  );

  if (areAllScheduled) {
    return AlertStatus.SCHEDULED;
  }

  const hasPendingStatus = groupStatuses.some(
    (status) => status === AlertStatus.PENDING,
  );

  if (hasPendingStatus) {
    return AlertStatus.PENDING;
  }

  const areAllLive = groupStatuses.every((status) =>
    liveStatuses.includes(status),
  );

  if (areAllLive) {
    return AlertStatus.LIVE;
  }

  const areAllFailed = groupStatuses.every(
    (status) => status === AlertStatus.FAILED,
  );

  if (areAllFailed) {
    return AlertStatus.FAILED;
  }

  const areAllTerminal = groupStatuses.every((status) =>
    terminalStatuses.includes(status),
  );

  if (areAllTerminal) {
    return AlertStatus.PAST;
  }

  const hasLiveStatus = groupStatuses.some((status) =>
    liveStatuses.includes(status),
  );

  const hasPartialStatus = groupStatuses.some((status) =>
    partialStatuses.includes(status),
  );

  const hasTerminalStatus = groupStatuses.some((status) =>
    terminalStatuses.includes(status),
  );

  if (hasPartialStatus || (hasLiveStatus && hasTerminalStatus)) {
    return AlertStatus.PARTIALLY_LIVE;
  }

  return AlertStatus.PARTIALLY_LIVE;
};

export const getActiveGroupCount = (groupStatuses: string[]): number => {
  return groupStatuses.filter((status) =>
    [AlertStatus.LIVE, AlertStatus.PARTIALLY_LIVE].includes(status),
  ).length;
};

export const getGroupCountLabel = (
  groupCount: number,
  activeGroupCount: number,
): string => {
  if (groupCount === 1) {
    return "1 group";
  }

  return `${groupCount} groups${
    activeGroupCount > 0 ? ` (${activeGroupCount} active)` : ""
  }`;
};

export const formatCreatedTimeLabel = (
  creationTime?: string | Date,
  startDateTime?: string | Date,
): string => {
  const currentTime = new Date().getTime();

  if (startDateTime) {
    const startTime = new Date(startDateTime).getTime();

    if (!Number.isNaN(startTime) && startTime > currentTime) {
      return `in ${formatTimeLeft(startTime - currentTime)}`;
    }
  }

  if (!creationTime) {
    return "";
  }

  const createdTime = new Date(creationTime).getTime();

  if (Number.isNaN(createdTime)) {
    return "";
  }

  return `${formatTimeLeft(currentTime - createdTime)} ago`;
};

const EMPTY_GROUPS_ARRAY: IListGroupItem[] = [];
export const getGroupsListFromState = (groupState: any): IListGroupItem[] => {
  const groups = groupState?.groups || EMPTY_GROUPS_ARRAY;
  return Array.isArray(groups) ? groups : EMPTY_GROUPS_ARRAY;
};

export const buildGroupMapByOrgId = (
  groupsList: IListGroupItem[] = [],
): Map<string, IListGroupItem> => {
  const groupMapByOrgId = new Map<string, IListGroupItem>();

  groupsList.forEach((group) => {
    if (group?.orgId) {
      groupMapByOrgId.set(group.orgId, group);
    }
  });

  return groupMapByOrgId;
};

export const buildGroupOrderMap = (
  groupsList: IListGroupItem[] = [],
): Map<string, number> => {
  const groupOrderMap = new Map<string, number>();

  groupsList.forEach((group, index) => {
    if (group?.orgId) {
      groupOrderMap.set(group.orgId, index);
    }
  });

  return groupOrderMap;
};

export const sortGroupRowsByListGroupOrder = (
  groupRows: IAlertGroup[],
  groupOrderMap: Map<string, number>,
): IAlertGroup[] => {
  return groupRows
    .sort((firstGroup, secondGroup) => {
      const firstIndex =
        groupOrderMap.get(firstGroup.groupId) ?? Number.MAX_SAFE_INTEGER;
      const secondIndex =
        groupOrderMap.get(secondGroup.groupId) ?? Number.MAX_SAFE_INTEGER;

      if (firstIndex !== secondIndex) {
        return firstIndex - secondIndex;
      }

      return (firstGroup.groupName || "").localeCompare(
        secondGroup.groupName || "",
      );
    })
    .map((groupRow) => ({
      ...groupRow,
      subRows: groupRow.subRows
        ? sortGroupRowsByListGroupOrder(groupRow.subRows, groupOrderMap)
        : [],
    }));
};

export const applyHierarchyLevel = (
  groupRows: IAlertGroup[],
  level: number = 1,
): IAlertGroup[] => {
  return groupRows.map((groupRow) => ({
    ...groupRow,
    hierarchyLevel: level,
    subRows: groupRow.subRows
      ? applyHierarchyLevel(groupRow.subRows, level + 1)
      : [],
  }));
};

export const flattenGroupRows = (
  groupRows: IAlertGroup[] = [],
): IAlertGroup[] => {
  return groupRows.flatMap((groupRow) => [
    groupRow,
    ...flattenGroupRows(groupRow.subRows || []),
  ]);
};

export const getUniqueAlertGroupsByGroupId = (
  alertGroups: IAlertGroup[] = [],
): IAlertGroup[] => {
  const uniqueGroupsMap = new Map<string, IAlertGroup>();

  alertGroups.forEach((group) => {
    if (!group.groupId) {
      return;
    }

    const existingGroup = uniqueGroupsMap.get(group.groupId);

    if (!existingGroup) {
      uniqueGroupsMap.set(group.groupId, group);
      return;
    }

    const existingCreationTime = existingGroup.creationTime
      ? new Date(existingGroup.creationTime).getTime()
      : 0;

    const currentCreationTime = group.creationTime
      ? new Date(group.creationTime).getTime()
      : 0;

    if (currentCreationTime > existingCreationTime) {
      uniqueGroupsMap.set(group.groupId, group);
    }
  });

  return Array.from(uniqueGroupsMap.values());
};

export const buildSelectedGroupHierarchyTree = (
  alertGroups: IAlertGroup[] = [],
  groupsList: IListGroupItem[] = [],
  alertDetails: IAlertDetail,
  alertId: string,
): IAlertGroup[] => {
  const groupMapByOrgId = buildGroupMapByOrgId(groupsList);
  const groupOrderMap = buildGroupOrderMap(groupsList);

  const groupRows: IAlertGroup[] = alertGroups.map((group) => {
    const groupDetails = groupMapByOrgId.get(group.groupId);
    const groupStatus = calculateGroupStatus(alertDetails, group);

    const groupName = group.groupName || groupDetails?.displayName || "";
    const groupDisplayName = formatGroupHierarchyLabel(
      groupDetails?.hierarchy,
      groupName,
    );

    return {
      ...group,
      alertId,
      alertHistoryId: group._id,
      groupName,
      groupDisplayName,
      parentGroupId: groupDetails?.parent,
      groupHierarchy: groupDetails?.hierarchy,
      parentAlertDetails: alertDetails,
      isChildRow: true,
      groupStatus,
      subRows: [],
    };
  });

  const selectedGroupIds = new Set(groupRows.map((group) => group.groupId));
  const firstRowByGroupId = new Map<string, IAlertGroup>();

  groupRows.forEach((groupRow) => {
    if (!firstRowByGroupId.has(groupRow.groupId)) {
      firstRowByGroupId.set(groupRow.groupId, groupRow);
    }
  });

  const topLevelRows: IAlertGroup[] = [];

  groupRows.forEach((groupRow) => {
    const groupDetails = groupMapByOrgId.get(groupRow.groupId);
    const parentGroupId = groupDetails?.parent;

    const parentRow =
      parentGroupId && selectedGroupIds.has(parentGroupId)
        ? firstRowByGroupId.get(parentGroupId)
        : null;

    if (parentRow && parentRow.groupId !== groupRow.groupId) {
      parentRow.subRows = parentRow.subRows || [];
      parentRow.subRows.push(groupRow);
      return;
    }

    topLevelRows.push(groupRow);
  });

  const sortedRows = sortGroupRowsByListGroupOrder(topLevelRows, groupOrderMap);
  return applyHierarchyLevel(sortedRows);
};

export const getSearchPlaceholderGroupRow = (
  alertId: string,
  alertDetails: IAlertDetail,
): IAlertGroup =>
  ({
    _id: `${alertId}-search-placeholder`,
    alertId,
    alertHistoryId: `${alertId}-search-placeholder`,
    groupId: `${alertId}-search-placeholder`,
    groupName: "",
    parentAlertDetails: alertDetails,
    isChildRow: true,
    isSearchPlaceholderRow: true,
    groupStatus: "",
    subRows: [],
  }) as IAlertGroup;

export const normalizeGroupedAlerts = (
  alerts: IAlertsApi[] = [],
  groupsList: IListGroupItem[] = [],
  isGlobalSearchActive: boolean = false,
): IAlertsApi[] => {
  return alerts.map((alert) => {
    const alertDetails = getAlertDetails(alert.alertDetails);
    const rawGroups = alert.groups || [];
    const groups = getUniqueAlertGroupsByGroupId(rawGroups);
    const firstGroup = groups[0];

    if (isGlobalSearchActive) {
      return {
        ...alert,

        _id: alert.alertId,
        templateId: firstGroup?.templateId || alert.templateId,
        groupId: firstGroup?.groupId || alert.groupId || "",
        groupName: firstGroup?.groupName || alert.groupName || "",
        jobId:
          firstGroup?.jobId || firstGroup?.groupAlertId || alert.jobId || "",
        alertStatus: firstGroup?.alertStatus || alert.alertStatus,
        successDevices:
          firstGroup?.successDevices || alert.successDevices || [],
        failedDevices: firstGroup?.failedDevices || alert.failedDevices || [],
        failedDeviceDetails:
          firstGroup?.failedDeviceDetails || alert.failedDeviceDetails || [],

        alertDetails,
        groups: [],
        isParentRow: true,
        isSearchResultRow: true,
        isSearchDetailsLoaded: false,
        aggregateStatus: "",
        groupCount: 0,
        activeGroupCount: 0,
        groupCountLabel: "",
        createdLabel: formatCreatedTimeLabel(
          alert.creationTime,
          alertDetails.startDateTime,
        ),

        subRows: [getSearchPlaceholderGroupRow(alert.alertId, alertDetails)],
      };
    }

    const hierarchyTreeRows = buildSelectedGroupHierarchyTree(
      groups,
      groupsList,
      alertDetails,
      alert.alertId,
    );

    const flattenedGroupRows = flattenGroupRows(hierarchyTreeRows);

    const groupStatuses = flattenedGroupRows.map(
      (group) => group.groupStatus || "",
    );

    const aggregateStatus = calculateAggregateStatus(groupStatuses);
    const groupCount = groups.length;
    const activeGroupCount = getActiveGroupCount(groupStatuses);

    return {
      ...alert,

      _id: alert.alertId,
      templateId: firstGroup?.templateId || alert.templateId,
      groupId: firstGroup?.groupId || alert.groupId || "",
      groupName: firstGroup?.groupName || alert.groupName || "",
      jobId: firstGroup?.jobId || firstGroup?.groupAlertId || alert.jobId || "",
      alertStatus: firstGroup?.alertStatus || alert.alertStatus,
      successDevices: firstGroup?.successDevices || alert.successDevices || [],
      failedDevices: firstGroup?.failedDevices || alert.failedDevices || [],
      failedDeviceDetails:
        firstGroup?.failedDeviceDetails || alert.failedDeviceDetails || [],
      alertDetails,
      groups: flattenedGroupRows,
      isParentRow: true,
      aggregateStatus,
      groupCount,
      activeGroupCount,
      groupCountLabel: getGroupCountLabel(groupCount, activeGroupCount),
      createdLabel: formatCreatedTimeLabel(
        alert.creationTime,
        alertDetails.startDateTime,
      ),

      subRows: groupCount > 0 ? hierarchyTreeRows : [],
    };
  });
};
